import React, { Suspense, lazy } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";

import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider, useAuth } from "./context/AuthContext";

import "./App.css";
import Footer from "./components/Footer";
import Navbar from "./components/Navbar";
import PageTransition from "./ui/PageTransition";

/* Route-level code splitting: each page (and the heavy libraries only it
   uses, e.g. html2canvas on the calendar/horários pages) loads on first
   visit instead of shipping in the initial bundle. */
const HomePage = lazy(() => import("./pages/HomePage"));
const DataPage = lazy(() => import("./pages/DataPage"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const ModelPage = lazy(() => import("./pages/ModelPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationPage"));
const HorariosPage = lazy(() => import("./pages/HorariosPage"));
const StatsPage = lazy(() => import("./pages/StatsPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const ApiKeysPage = lazy(() => import("./pages/ApiKeysPage"));
const EmployeeCalendarPage = lazy(() => import("./pages/EmployeeCalendarPage"));
const EmployeeProfilePage = lazy(() => import("./pages/EmployeeProfilePage"));

const ADMIN_LINKS = [
  { to: "/data", label: "Dados" },
  { to: "/model", label: "Modelo IA" },
  { to: "/calendar", label: "Calendário" },
  { to: "/horarios", label: "Horários" },
  { to: "/stats", label: "Estatísticas" },
  { to: "/notifications", label: "Notificações" },
];

/* Shown while a lazy route's chunk downloads. Kept minimal: the shell
   (navbar/footer) stays mounted, so this only fills the content region. */
function RouteFallback() {
  return (
    <div className="app-route-loading" role="status" aria-label="A carregar página">
      <span className="gc-spinner" aria-hidden="true" />
    </div>
  );
}

// Shared chrome for every authenticated layout. Purely presentational — the
// routes and guards below are untouched; the routed element still arrives via
// <Outlet />, now wrapped so each navigation gets the standard page entrance.
function AppShell({ links, homeTo }) {
  const location = useLocation();

  return (
    <div className="app-wrapper">
      <Navbar links={links} homeTo={homeTo} />
      <main className="app-content">
        <PageTransition key={location.pathname}>
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </PageTransition>
      </main>
      <Footer />
    </div>
  );
}

// Layout for Admin / Recursos Humanos
function AppLayout() {
  const { user } = useAuth();
  // Utilizadores and Chaves API administer accounts and credentials, so they
  // are Admin-only in the nav and behind an Admin-only route guard below.
  const links =
    user?.role === "Admin"
      ? [
          ...ADMIN_LINKS,
          { to: "/users", label: "Utilizadores" },
          { to: "/api-keys", label: "Chaves API" },
        ]
      : ADMIN_LINKS;

  return <AppShell links={links} homeTo="/" />;
}

// Layout for Cuidadores — only shows Calendário and Perfil
function CuidadoresLayout() {
  const links = [
    { to: "/meu-horario", label: "Calendário" },
    { to: "/perfil", label: "Perfil" },
  ];
  return <AppShell links={links} homeTo="/meu-horario" />;
}

// Redirects based on role after authentication
function HomeRedirect() {
  const { user } = useAuth();
  if (user?.role === "Cuidadores") {
    return <Navigate to="/meu-horario" replace />;
  }
  return <HomePage />;
}

// Guards a route so only Admin/HR can access it; Cuidadores get redirected
function AdminHROnly({ children }) {
  const { user } = useAuth();
  if (user?.role === "Cuidadores") {
    return <Navigate to="/meu-horario" replace />;
  }
  return children;
}

// Guards a route so only Cuidadores can access it; others get redirected
function CuidadoresOnly({ children }) {
  const { user } = useAuth();
  if (user?.role !== "Cuidadores") {
    return <Navigate to="/" replace />;
  }
  return children;
}

// Renders the right layout based on role
function RoleBasedLayout() {
  const { user } = useAuth();
  if (user?.role === "Cuidadores") return <CuidadoresLayout />;
  return <AppLayout />;
}

function AppRoutes() {
  return (
    // Outer boundary covers the routes that render OUTSIDE the shell
    // (login/register); in-shell pages suspend at AppShell's inner boundary
    // so the navbar and footer never blink out during a chunk load.
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />

      <Route element={<ProtectedRoute />}>
        <Route element={<RoleBasedLayout />}>
          {/* Root — redirects based on role */}
          <Route path="/" element={<HomeRedirect />} />

          {/* Admin / HR only pages */}
          <Route path="/data" element={<AdminHROnly><DataPage /></AdminHROnly>} />
          <Route path="/model" element={<AdminHROnly><ModelPage /></AdminHROnly>} />
          <Route path="/horarios" element={<AdminHROnly><HorariosPage /></AdminHROnly>} />
          <Route path="/stats" element={<AdminHROnly><StatsPage /></AdminHROnly>} />
          <Route path="/calendar" element={<AdminHROnly><CalendarPage /></AdminHROnly>} />
          <Route path="/notifications" element={<AdminHROnly><NotificationsPage /></AdminHROnly>} />

          {/* Admin only — account and credential administration */}
          <Route element={<ProtectedRoute allowedRoles={["Admin"]} />}>
            <Route path="/users" element={<UsersPage />} />
            <Route path="/api-keys" element={<ApiKeysPage />} />
          </Route>

          {/* Cuidadores only pages */}
          <Route path="/meu-horario" element={<CuidadoresOnly><EmployeeCalendarPage /></CuidadoresOnly>} />
          <Route path="/perfil" element={<CuidadoresOnly><EmployeeProfilePage /></CuidadoresOnly>} />
        </Route>
      </Route>
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
